// ── Team fixtures ────────────────────────────────────────────────────────────
// How a tour's squads, teams, fixtures and itinerary fit together:
//
//   Team Info squads  (requests/{id}/teamInfo/data .squads[])
//     The customer's form: years of birth, gender, kit. Each squad has an id
//     by position ("squad-1", "squad-2"…) so a re-submitted form, which can't
//     read what was saved before, still lines up with the same teams.
//
//   Teams tab teams   (requests/{id}/teams/layout .teams[])
//     One team per squad, created automatically with id "team-<squadId>" so
//     two people syncing at once write the same thing. The coach names it and
//     places players later. Hand-made teams (no squadId) still work.
//
//   Fixtures          (requests/{id}/fixtures/{fixtureId})
//     One match: which team, when, where, against whom. Read by everyone who
//     can see the tour (customers, suppliers with fixture access).
//
//   Fixture contacts  (fixtureContacts/{fixtureId}, top level)
//     Opposition coach + referee. Staff and tour leaders only — kept out of
//     the tour's subcollections because the rules' catch-all lets any
//     signed-in user read those.
//
//   Itinerary         (requests/{id}/itinerary/{itemId} .fixtureIds[])
//     A "Fixtures" activity points at fixtures by id, so editing a fixture in
//     Team Info or on the itinerary changes the one record both show.

import {
  collection, doc, addDoc, deleteDoc, getDoc, runTransaction, serverTimestamp,
  setDoc, updateDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { seasonStartYear, spanishCategory } from './fixtureFinder';
import { englishT } from '../contexts/LocaleContext';

// ── Squads ───────────────────────────────────────────────────────────────────

export function squadIdAt(index) {
  return `squad-${index + 1}`;
}

/** Squads with an id each — saved ids kept, older squads get their position's. */
export function squadsWithIds(squads) {
  const list = Array.isArray(squads) ? squads : [];
  const used = new Set(list.map((s) => s && s.id).filter(Boolean));
  return list.map((s, i) => {
    if (s && s.id) return s;
    let id = squadIdAt(i);
    for (let n = i + 1; used.has(id); n += 1) id = squadIdAt(n);
    used.add(id);
    return { ...s, id };
  });
}

/** The next free squad id, for a squad added after the form was submitted. */
export function nextSquadId(squads) {
  const used = new Set((squads || []).map((s) => s && s.id).filter(Boolean));
  let n = (squads || []).length;
  while (used.has(squadIdAt(n))) n += 1;
  return squadIdAt(n);
}

// ── Age labels ───────────────────────────────────────────────────────────────

function birthYears(sq) {
  const raw = Array.isArray(sq?.yearsOfBirth) && sq.yearsOfBirth.length
    ? sq.yearsOfBirth
    : (sq?.yearOfBirth ? [sq.yearOfBirth] : []);
  return [...new Set(raw.map(Number).filter((y) => Number.isFinite(y) && y > 1900))].sort((a, b) => a - b);
}

function tourDate(request) {
  const d = request?.departureDate ? new Date(`${String(request.departureDate).slice(0, 10)}T00:00:00`) : null;
  return d && !Number.isNaN(d.getTime()) ? d : new Date();
}

function range(values) {
  if (values.length === 0) return '';
  return values.length === 1 ? String(values[0]) : `${values[0]}–${values[values.length - 1]}`;
}

/** UK age group for a birth year: under-N on 31 August of the season's start. */
export function ukAgeGroup(birthYear, date = new Date()) {
  const n = seasonStartYear(date) - Number(birthYear) + 1;
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Whether a tour shows the Spanish category alongside the UK age group. */
export function showsSpanishCategory(request) {
  return (request?.brand || '') !== 'padelante';
}

/**
 * Age details for a squad on this tour, e.g.
 * { born: '2012–2013', uk: 'U14–U15', es: 'Cadete / Infantil', gender: 'Male',
 *   label: 'U14–U15 · Cadete / Infantil · Male · born 2012–2013' }.
 * Empty strings where the squad doesn't say.
 */
export function squadAgeInfo(sq, request) {
  const years = birthYears(sq);
  const date = tourDate(request);
  const ukNums = [...new Set(years.map((y) => ukAgeGroup(y, date)).filter(Boolean))].sort((a, b) => a - b);
  const uk = ukNums.length ? (ukNums.length === 1 ? `U${ukNums[0]}` : `U${ukNums[0]}–U${ukNums[ukNums.length - 1]}`) : '';
  // Oldest category first, as the fixture finder writes it.
  const es = showsSpanishCategory(request) && years.length
    ? [...new Set(years.map((y) => spanishCategory(y, date)))].join(' / ')
    : '';
  const gender = sq?.gender || '';
  const born = range(years);
  const label = [uk, es, gender, born && `born ${born}`].filter(Boolean).join(' · ');
  return { born, uk, es, gender, label };
}

// ── Teams ↔ squads ───────────────────────────────────────────────────────────

export function teamIdForSquad(squadId) {
  return `team-${squadId}`;
}

/**
 * Bring the Teams tab in line with Team Info's squads. Pure: returns
 * { teams, assignments, changed }.
 *
 *  - every squad gets a team (named after the squad) unless one is linked —
 *    except on tours whose teams were made by hand before squads were linked,
 *    where staff link them instead of getting a second set;
 *  - linked teams carry the squad's age label (cached for the no-login coach
 *    page, which can't read Team Info);
 *  - a team whose squad has gone is removed if nobody is in it and it has no
 *    fixtures, otherwise flagged so staff decide. `fixtureTeamIds` null means
 *    "fixtures unknown", which never removes.
 */
export function syncTeamsWithSquads({ teams = [], assignments = {}, squads = [], request = null, fixtureTeamIds = null }) {
  const withIds = squadsWithIds(squads);
  const squadById = Object.fromEntries(withIds.map((s) => [s.id, s]));
  const hasHandMadeTeams = teams.some((t) => !t.squadId);
  let changed = false;

  const nextTeams = [];
  const nextAssignments = { ...assignments };
  for (const t of teams) {
    if (!t.squadId) { nextTeams.push(t); continue; }
    const sq = squadById[t.squadId];
    if (sq) {
      const ageLabel = squadAgeInfo(sq, request).label;
      if (t.ageLabel !== ageLabel || t.squadRemoved) {
        const { squadRemoved, ...rest } = t;
        nextTeams.push({ ...rest, ageLabel });
        changed = true;
      } else {
        nextTeams.push(t);
      }
      continue;
    }
    const empty = (assignments[t.id] || []).length === 0;
    if (empty && fixtureTeamIds && !fixtureTeamIds.has(t.id)) {
      delete nextAssignments[t.id];
      changed = true;
      continue;
    }
    if (!t.squadRemoved) { nextTeams.push({ ...t, squadRemoved: true }); changed = true; } else nextTeams.push(t);
  }

  if (!hasHandMadeTeams) {
    const linked = new Set(nextTeams.map((t) => t.squadId).filter(Boolean));
    withIds.forEach((sq, i) => {
      if (linked.has(sq.id)) return;
      const id = teamIdForSquad(sq.id);
      if (nextTeams.some((t) => t.id === id)) return;
      nextTeams.push({ id, name: sq.label || `Squad ${i + 1}`, squadId: sq.id, ageLabel: squadAgeInfo(sq, request).label });
      if (!nextAssignments[id]) nextAssignments[id] = [];
      changed = true;
    });
  }

  return { teams: nextTeams, assignments: nextAssignments, changed };
}

/** Squads that no team is linked to. */
export function unlinkedSquads(teams, squads) {
  const linked = new Set((teams || []).map((t) => t.squadId).filter(Boolean));
  return squadsWithIds(squads).filter((s) => !linked.has(s.id));
}

/**
 * Teams list for display: each linked team's age label and kit worked out live
 * from its squad, so a Team Info edit shows straight away everywhere.
 */
export function teamsWithAges(teams, squads, request) {
  const squadById = Object.fromEntries(squadsWithIds(squads).map((s) => [s.id, s]));
  return (teams || []).map((t) => {
    const sq = t.squadId && squadById[t.squadId];
    return sq ? { ...t, ageLabel: squadAgeInfo(sq, request).label, kit: squadKit(sq) } : t;
  });
}

// ── Kit ──────────────────────────────────────────────────────────────────────
// From the squad's Team Info kit, so organisers and tour leaders can spot a
// clash with the opposition before match day.

const KIT_HEX = {
  white: '#FFFFFF', black: '#111827', grey: '#6B7280', red: '#DC2626', blue: '#1D4ED8',
  yellow: '#FBBF24', green: '#16A34A', orange: '#EA580C', navy: '#1e3a5f', maroon: '#7f1d1d',
  'sky blue': '#38bdf8', purple: '#7c3aed', pink: '#ec4899',
};

/** Swatch colour for a kit colour name (or a custom hex), or null. */
export function kitColourHex(name) {
  if (!name) return null;
  const v = String(name).trim();
  return v.startsWith('#') ? v : KIT_HEX[v.toLowerCase()] || null;
}

const PATTERN_LABEL = { stripes: 'stripes', hoops: 'hoops', halves: 'halves' };

function kitPart(prefix, sq) {
  const part = {
    jersey: sq[`${prefix}JerseyColour`] || '',
    jersey2: sq[`${prefix}JerseyColour2`] || '',
    pattern: sq[`${prefix}JerseyPattern`] || 'plain',
    shorts: sq[`${prefix}ShortsColour`] || '',
    socks: sq[`${prefix}SocksColour`] || '',
  };
  return part.jersey || part.shorts || part.socks ? part : null;
}

/** A squad's { home, away } kit (away null for "one kit only"), or null if none given. */
export function squadKit(sq) {
  if (!sq) return null;
  const home = kitPart('home', sq);
  const away = sq.onlyOneKit ? null : kitPart('away', sq);
  return home || away ? { home, away } : null;
}

/** "Red & White stripes" — just the shirt. `t` (useT) translates; English without it. */
export function shirtText(part, t = englishT) {
  if (!part?.jersey) return '';
  const pattern = part.pattern !== 'plain' && PATTERN_LABEL[part.pattern] ? ` ${t.value('pattern', PATTERN_LABEL[part.pattern])}` : '';
  const c1 = t.value('colour', part.jersey);
  return part.jersey2 && part.pattern !== 'plain' ? `${c1} & ${t.value('colour', part.jersey2)}${pattern}` : `${c1}${pattern}`;
}

/** "Red & White stripes shirt, Black shorts, Red socks". */
export function kitPartText(part, t = englishT) {
  if (!part) return '';
  return [
    part.jersey && t('fixtures.shirt', { c: shirtText(part, t) }),
    part.shorts && t('fixtures.shorts', { c: t.value('colour', part.shorts) }),
    part.socks && t('fixtures.socks', { c: t.value('colour', part.socks) }),
  ].filter(Boolean).join(', ');
}

/** "Home: Red shirt · Away: Blue shirt" — shirts only, for one-line uses (PDF). */
export function kitShortText(kit) {
  if (!kit) return '';
  return [
    kit.home?.jersey && `Home ${shirtText(kit.home)}`,
    kit.away?.jersey && `Away ${shirtText(kit.away)}`,
  ].filter(Boolean).join(' / ');
}

const sameColour = (a, b) => !!a && !!b && String(a).trim().toLowerCase() === String(b).trim().toLowerCase();

/**
 * A kit-clash hint for a fixture, or '' — when the opposition's main colour
 * matches our shirt. Only the main shirt colours are compared, so it's a
 * prompt to check, not a ruling.
 */
export function kitClash(fixture, kit, t = englishT) {
  const opp = fixture?.opponentPrimaryColour;
  if (!opp || !kit) return '';
  const homeClash = sameColour(opp, kit.home?.jersey);
  const awayClash = sameColour(opp, kit.away?.jersey);
  const c = t.value('colour', opp);
  if (homeClash && kit.away?.jersey && !awayClash) return t('fixtures.clashAway', { c });
  if (homeClash && awayClash) return t('fixtures.clashBoth', { c });
  if (homeClash) return t('fixtures.clashNoAway', { c });
  return '';
}

export function teamsLayoutDoc(requestId) {
  return doc(db, 'requests', requestId, 'teams', 'layout');
}

/**
 * Run the sync against the saved layout in a transaction (so a concurrent
 * rename or move isn't lost). No write when nothing changes.
 */
export async function syncTeamsLayout(requestId, { squads, request, fixtureTeamIds = null, user = null }) {
  const ref = teamsLayoutDoc(requestId);
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const data = snap.exists() ? snap.data() : {};
    const result = syncTeamsWithSquads({
      teams: data.teams || [], assignments: data.assignments || {}, squads, request, fixtureTeamIds,
    });
    if (!result.changed) return false;
    tx.set(ref, {
      teams: result.teams,
      assignments: result.assignments,
      updatedAt: serverTimestamp(),
      updatedBy: user?.email || '',
    }, { merge: true });
    return true;
  });
}

/** Link (or unlink, squadId null) a hand-made team to a squad. */
export async function linkTeamToSquad(requestId, teamId, squadId, { squads, request }) {
  const ref = teamsLayoutDoc(requestId);
  const sq = squadsWithIds(squads).find((s) => s.id === squadId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const teams = (snap.exists() ? snap.data().teams : []) || [];
    tx.set(ref, {
      teams: teams.map((t) => {
        if (t.id !== teamId) return t;
        const { squadId: _old, ageLabel: _age, squadRemoved: _gone, ...rest } = t;
        return sq ? { ...rest, squadId: sq.id, ageLabel: squadAgeInfo(sq, request).label } : rest;
      }),
      updatedAt: serverTimestamp(),
    }, { merge: true });
  });
}

/** Give a team to every squad still without one (tours with hand-made teams). */
export async function addTeamsForUnlinkedSquads(requestId, { squads, request }) {
  const ref = teamsLayoutDoc(requestId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const data = snap.exists() ? snap.data() : {};
    const teams = [...(data.teams || [])];
    const assignments = { ...(data.assignments || {}) };
    for (const sq of unlinkedSquads(teams, squads)) {
      const id = teamIdForSquad(sq.id);
      if (teams.some((t) => t.id === id)) continue;
      teams.push({ id, name: sq.label || id, squadId: sq.id, ageLabel: squadAgeInfo(sq, request).label });
      assignments[id] = assignments[id] || [];
    }
    tx.set(ref, { teams, assignments, updatedAt: serverTimestamp() }, { merge: true });
  });
}

// ── Fixtures ─────────────────────────────────────────────────────────────────

/** The itinerary activity type that carries a Matches list. */
export function isFixturesActivity(type) {
  return /^fixtures?$/i.test(String(type || '').trim());
}

export const FIXTURE_FIELDS = [
  'teamId', 'date', 'time', 'endTime',
  'venueName', 'venueAddress', 'venueGoogleLink',
  'opponentName', 'opponentPrimaryColour', 'opponentSecondaryColour',
  'opponentYearsOfBirth', 'opponentAbility', 'notes',
];

export function blankFixture(overrides = {}) {
  return {
    teamId: '', date: '', time: '', endTime: '',
    venueName: '', venueAddress: '', venueGoogleLink: '',
    opponentName: '', opponentPrimaryColour: '', opponentSecondaryColour: '',
    opponentYearsOfBirth: [], opponentAbility: '', notes: '',
    ...overrides,
  };
}

export function blankContacts() {
  return { oppositionCoach: { name: '', email: '', phone: '' }, referee: { name: '', email: '', phone: '' } };
}

function cleanPerson(p) {
  return { name: (p?.name || '').trim(), email: (p?.email || '').trim(), phone: (p?.phone || '').trim() };
}

export function hasContacts(c) {
  return !!c && ['oppositionCoach', 'referee'].some((k) => {
    const p = c[k] || {};
    return p.name || p.email || p.phone;
  });
}

/** Only the fixture's own fields, trimmed — so stray UI state never gets saved. */
export function cleanFixture(f) {
  const out = {};
  for (const k of FIXTURE_FIELDS) {
    const v = f?.[k];
    if (k === 'opponentYearsOfBirth') out[k] = Array.isArray(v) ? v.map(Number).filter(Number.isFinite) : [];
    else out[k] = typeof v === 'string' ? v.trim() : (v ?? '');
  }
  return out;
}

export function fixturesCol(requestId) {
  return collection(db, 'requests', requestId, 'fixtures');
}

export function fixtureContactsDoc(fixtureId) {
  return doc(db, 'fixtureContacts', fixtureId);
}

/**
 * Create or update a fixture, and (for staff / tour leaders) its contacts.
 * `contacts` undefined leaves them alone. Returns the fixture id.
 */
export async function saveFixture(requestId, fixtureId, fixture, { contacts, user } = {}) {
  const data = { ...cleanFixture(fixture), updatedAt: serverTimestamp(), updatedBy: user?.email || '' };
  let id = fixtureId;
  if (id) {
    await updateDoc(doc(db, 'requests', requestId, 'fixtures', id), data);
  } else {
    id = (await addDoc(fixturesCol(requestId), { ...data, createdAt: serverTimestamp() })).id;
  }
  if (contacts !== undefined) {
    await setDoc(fixtureContactsDoc(id), {
      requestId,
      oppositionCoach: cleanPerson(contacts.oppositionCoach),
      referee: cleanPerson(contacts.referee),
      updatedAt: serverTimestamp(),
    });
  }
  return id;
}

/**
 * Delete a fixture and its contacts. Itinerary activities keep its id and show
 * "Fixture removed" to staff until they take it off, so a removal never goes
 * unnoticed; customers and the PDF just skip it.
 */
export async function deleteFixture(requestId, fixtureId) {
  await deleteDoc(doc(db, 'requests', requestId, 'fixtures', fixtureId));
  await deleteDoc(fixtureContactsDoc(fixtureId)).catch(() => {});
}

export async function loadFixtureContacts(fixtureId) {
  const snap = await getDoc(fixtureContactsDoc(fixtureId));
  return snap.exists() ? snap.data() : null;
}

// ── Display helpers ──────────────────────────────────────────────────────────

/** Matches sorted by kick-off, then team name. */
export function sortFixtures(fixtures, teamsById = {}) {
  return [...(fixtures || [])].sort((a, b) =>
    (a.date || '').localeCompare(b.date || '')
    || (a.time || '').localeCompare(b.time || '')
    || (teamsById[a.teamId]?.name || '').localeCompare(teamsById[b.teamId]?.name || ''));
}

/** "U14s vs CD Altea" style one-liner (no time/venue). */
export function matchTitle(f, team) {
  return `${team?.name || 'Team TBC'} vs ${f.opponentName || 'Opposition TBC'}`;
}

/** The ISO date of an itinerary day (0-indexed) for a tour. */
export function itineraryDayDate(departureDate, dayIdx) {
  if (!departureDate) return '';
  const d = new Date(`${String(departureDate).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  d.setDate(d.getDate() + dayIdx);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Why a match no longer fits the itinerary activity it's on, or ''.
 * (Its date moved to another day, or it kicks off outside the activity.)
 */
export function fixtureMismatch(fixture, item, departureDate) {
  const dayDate = itineraryDayDate(departureDate, (item.day || 1) - 1);
  if (fixture.date && dayDate && fixture.date !== dayDate) {
    const d = new Date(`${fixture.date}T00:00:00`);
    return `Now on ${d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}`;
  }
  if (fixture.time && item.time && fixture.time < item.time) return `Kicks off ${fixture.time}, before this activity starts`;
  if (fixture.time && item.endTime && fixture.time >= item.endTime) return `Kicks off ${fixture.time}, after this activity ends`;
  return '';
}

export function fmtFixtureDate(iso, opts = { weekday: 'short', day: 'numeric', month: 'short' }, locale = 'en-GB') {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(locale, opts);
}

/**
 * A cached age label ("U14 · Infantil · Male · born 2012") in the viewer's
 * language: the gender and "born" translated, UK age groups as Sub-14 in Spanish.
 */
export function localAgeLabel(label, t = englishT) {
  if (!label || t.locale === 'en') return label || '';
  return label.split(' · ').map((part) => {
    if (part.startsWith('born ')) return t('squad.born', { years: part.slice(5) });
    if (/^U\d/.test(part)) return part.replace(/U(\d+)/g, 'Sub-$1');
    return t.value('gender', part);
  }).join(' · ');
}

/**
 * Plain-text match lines for an itinerary activity (PDF, exports), in kick-off
 * order and without contacts, e.g.
 * "U14s (U14 · Infantil) vs CD Altea (Red/White) · 10:00 · Pitch 1 · Our kit: Home Blue / Away White".
 * Fixtures deleted since are skipped.
 */
export function activityMatchLines(item, fixturesById = {}, teamsById = {}) {
  if (!isFixturesActivity(item?.activityType)) return [];
  const present = sortFixtures((item.fixtureIds || []).map((id) => fixturesById[id]).filter(Boolean), teamsById);
  return present.map((f) => {
    const team = teamsById[f.teamId];
    const age = team?.ageLabel ? ` (${team.ageLabel.split(' · born')[0]})` : '';
    const kit = [f.opponentPrimaryColour, f.opponentSecondaryColour].filter(Boolean).join('/');
    const ourKit = kitShortText(team?.kit);
    const clash = kitClash(f, team?.kit);
    return [
      `${team?.name || 'Team TBC'}${age} vs ${f.opponentName || 'Opposition TBC'}${kit ? ` (${kit})` : ''}`,
      f.time && `${f.time}${f.endTime ? `–${f.endTime}` : ''}`,
      f.venueName,
      ourKit && `Our kit: ${ourKit}`,
      clash && `Kit clash: ${clash}`,
    ].filter(Boolean).join(' · ');
  });
}
